import React, { useMemo } from 'react';
import {
  PiArrowDown,
  PiCaretDown,
  PiFunnelSimple,
  PiSquareSplitVertical,
  PiTable,
} from 'react-icons/pi';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type {
  PropertyActiveStatusFilter,
  PropertyOperationFilter,
  PropertySortBy,
  PropertyViewMode,
} from '../../types/property-filters.types';

interface PropertiesToolbarProps {
  activeFilterCount: number;
  operation: PropertyOperationFilter;
  activeStatus: PropertyActiveStatusFilter;
  isExpanded: boolean;
  searchValue: string;
  sortBy: PropertySortBy;
  viewMode: PropertyViewMode;
  onOpenMobileFilters: () => void;
  onOperationChange: (value: PropertyOperationFilter) => void;
  onActiveStatusChange: (value: PropertyActiveStatusFilter) => void;
  onSearchChange: (value: string) => void;
  onSortChange: (value: PropertySortBy) => void;
  onViewModeChange: (value: PropertyViewMode) => void;
}

export const PropertiesToolbar: React.FC<PropertiesToolbarProps> = ({
  activeFilterCount,
  operation,
  activeStatus,
  isExpanded,
  searchValue,
  sortBy,
  viewMode,
  onOpenMobileFilters,
  onOperationChange,
  onActiveStatusChange,
  onSearchChange,
  onSortChange,
  onViewModeChange,
}) => {
  const { t } = useI18n();
  const [isViewDropdownOpen, setIsViewDropdownOpen] = React.useState(false);
  const [isOperationDropdownOpen, setIsOperationDropdownOpen] =
    React.useState(false);
  const [isActiveStatusDropdownOpen, setIsActiveStatusDropdownOpen] =
    React.useState(false);

  const sortOptions = useMemo(
    () => [
      {
        id: 'UPDATED_DESC' as const,
        content: t('catalog.properties.toolbar.sort.updatedDesc'),
      },
      {
        id: 'CREATED_DESC' as const,
        content: t('catalog.properties.toolbar.sort.createdDesc'),
      },
      {
        id: 'PRICE_DESC' as const,
        content: t('catalog.properties.toolbar.sort.priceDesc'),
      },
      {
        id: 'PRICE_ASC' as const,
        content: t('catalog.properties.toolbar.sort.priceAsc'),
      },
    ],
    [t]
  );

  const filtersButtonLabel =
    activeFilterCount > 0
      ? t('catalog.properties.toolbar.filtersWithCount', {
          count: String(activeFilterCount),
        })
      : t('catalog.properties.toolbar.filters');
  const selectedSortLabel =
    sortOptions.find((option) => option.id === sortBy)?.content ??
    t('catalog.properties.toolbar.sort.updatedDesc');
  const operationOptions = useMemo(
    () => [
      {
        id: 'ALL' as const,
        content: t('catalog.properties.filters.operationAll'),
      },
      {
        id: 'SALE' as const,
        content: t('catalog.properties.filters.operationSale'),
      },
      {
        id: 'RENT' as const,
        content: t('catalog.properties.filters.operationRent'),
      },
      {
        id: 'TEMPORARY_RENT' as const,
        content: t('catalog.properties.filters.operationTemporaryRent'),
      },
    ],
    [t]
  );
  const selectedOperationLabel =
    operationOptions.find((option) => option.id === operation)?.content ??
    t('catalog.properties.filters.operationAll');
  const activeStatusOptions = useMemo(
    () => [
      {
        id: 'ACTIVE' as const,
        content: t('catalog.properties.filters.activeStatusActive'),
      },
      {
        id: 'INACTIVE' as const,
        content: t('catalog.properties.filters.activeStatusInactive'),
      },
      {
        id: 'ALL' as const,
        content: t('catalog.properties.filters.activeStatusAll'),
      },
    ],
    [t]
  );
  const selectedActiveStatusLabel =
    activeStatusOptions.find((option) => option.id === activeStatus)?.content ??
    t('catalog.properties.filters.activeStatusActive');
  const viewOptions = useMemo(
    () => [
      {
        id: 'VISUAL' as const,
        label: t('catalog.properties.toolbar.view.visual'),
        icon: <PiSquareSplitVertical className="h-4 w-4" />,
      },
      {
        id: 'COMPACT' as const,
        label: t('catalog.properties.toolbar.view.compact'),
        icon: <PiTable className="h-4 w-4" />,
      },
    ],
    [t]
  );
  const selectedView =
    viewOptions.find((option) => option.id === viewMode) ?? viewOptions[0];
  const renderViewDropdown = (isFullWidth: boolean) => (
    <Tooltip
      enabled={!isViewDropdownOpen}
      content={t('catalog.properties.toolbar.viewLabel')}
      placement="bottom"
      size="md"
    >
      <MenuDropdown
        items={viewOptions.map((option) => ({
          content: option.label,
          icon: option.icon,
          selected: option.id === viewMode,
          onClick: () => onViewModeChange(option.id),
        }))}
        icon={selectedView.icon}
        label={selectedView.label}
        variant="secondary"
        appearance="outline"
        size="md"
        className={
          isFullWidth
            ? 'w-full justify-between'
            : 'w-full justify-between sm:w-[176px]'
        }
        handlerContainerClassName={isFullWidth ? 'w-full' : ''}
        placement="bottom-start"
        onOpenChange={setIsViewDropdownOpen}
      />
    </Tooltip>
  );

  if (!isExpanded) {
    return null;
  }

  return (
    <div className="w-full min-w-0">
      <div className="flex flex-col gap-3">
        <div className="sm:hidden">{renderViewDropdown(true)}</div>

        <div className="hidden sm:grid sm:grid-cols-[176px_minmax(0,1fr)] sm:items-end sm:gap-3 lg:grid-cols-[176px_minmax(0,1fr)_176px]">
          <div>{renderViewDropdown(false)}</div>

          <div className="min-w-0">
            <LabelTextInput
              label={t('catalog.properties.filters.searchLabel')}
              size="md"
              labelPosition="top"
              inputProps={{
                placeholder: t('catalog.properties.filters.searchPlaceholder'),
                value: searchValue,
                onChange: (event) => onSearchChange(event.target.value),
              }}
            />
          </div>

          <div className="hidden lg:block min-w-0">
            <MenuDropdown
              icon={<PiArrowDown className="h-4 w-4" />}
              label={selectedSortLabel}
              items={sortOptions.map((option) => ({
                content: option.content,
                selected: option.id === sortBy,
                onClick: () => onSortChange(option.id),
              }))}
              variant="secondary"
              appearance="outline"
              size="md"
              className="w-[176px] justify-between"
              placement="bottom-start"
            />
          </div>
        </div>

        <div className="sm:hidden">
          <LabelTextInput
            label={t('catalog.properties.filters.searchLabel')}
            size="md"
            labelPosition="top"
            inputProps={{
              placeholder: t('catalog.properties.filters.searchPlaceholder'),
              value: searchValue,
              onChange: (event) => onSearchChange(event.target.value),
            }}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
          <div className="min-w-0">
            <Button
              type="button"
              variant="secondary"
              appearance="outline"
              size="md"
              className="relative w-full !justify-start gap-2 px-3 text-left"
              onClick={onOpenMobileFilters}
            >
              <PiFunnelSimple className="h-4 w-4" />
              <span className="whitespace-nowrap truncate">
                {filtersButtonLabel}
              </span>
              <PiCaretDown className="h-4 w-4 absolute right-3" />
            </Button>
          </div>

          <div className="min-w-0">
            <MenuDropdown
              icon={<PiArrowDown className="h-4 w-4" />}
              label={selectedSortLabel}
              items={sortOptions.map((option) => ({
                content: option.content,
                selected: option.id === sortBy,
                onClick: () => onSortChange(option.id),
              }))}
              variant="secondary"
              appearance="outline"
              size="md"
              className="w-full justify-between"
              placement="bottom-start"
            />
          </div>
        </div>

        <div className="hidden lg:flex lg:flex-wrap lg:items-center lg:justify-start lg:gap-2">
          <Tooltip
            enabled={!isOperationDropdownOpen}
            content={t('catalog.properties.filters.operationAll')}
            placement="bottom"
            size="md"
          >
            <MenuDropdown
              label={selectedOperationLabel}
              items={operationOptions.map((option) => ({
                content: option.content,
                selected: option.id === operation,
                onClick: () => onOperationChange(option.id),
              }))}
              variant="secondary"
              appearance="outline"
              size="md"
              className="w-[200px] justify-between"
              placement="bottom-start"
              onOpenChange={setIsOperationDropdownOpen}
            />
          </Tooltip>

          <Tooltip
            enabled={!isActiveStatusDropdownOpen}
            content={t('catalog.properties.filters.activeStatusActive')}
            placement="bottom"
            size="md"
          >
            <MenuDropdown
              label={selectedActiveStatusLabel}
              items={activeStatusOptions.map((option) => ({
                content: option.content,
                selected: option.id === activeStatus,
                onClick: () => onActiveStatusChange(option.id),
              }))}
              variant="secondary"
              appearance="outline"
              size="md"
              className="w-[180px] justify-between"
              placement="bottom-start"
              onOpenChange={setIsActiveStatusDropdownOpen}
            />
          </Tooltip>

          <Button
            type="button"
            variant="secondary"
            appearance="outline"
            size="md"
            className="relative w-[176px] min-w-[176px] !justify-start gap-2 px-3 text-left"
            onClick={onOpenMobileFilters}
          >
            <PiFunnelSimple className="h-4 w-4" />
            <span className="whitespace-nowrap truncate">
              {filtersButtonLabel}
            </span>
            <PiCaretDown className="absolute right-3 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
